import { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAssetStore } from '@/stores/assetStore';
import { useSettingsStore } from '@/stores/settingsStore';
import { unifiedApiService } from '@/services/unifiedApi';
import { useLifecycleTracker } from '@/utils/memoryLeak';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { useOffline } from '@/contexts/OfflineContext';
import { ApiError } from '@/types/api';
import { useAppVisibility } from '@/hooks/useAppVisibility';

export const useAssetData = () => {
  const { assets, updateAssetPrice, updateAssetPriceStatus } = useAssetStore();
  const { updateInterval } = useSettingsStore();
  const lifecycleTracker = useLifecycleTracker();
  const { handleError } = useErrorHandler();
  const { isOnline } = useOffline();
  const isAppVisible = useAppVisibility();

  // Track consecutive failures to disable auto-refresh after multiple failures
  const consecutiveFailuresRef = useRef(0);
  const [shouldPauseRefetch, setShouldPauseRefetch] = useState(false);
  // 使用 ref 来立即阻止查询，不等待 state 更新
  const shouldPauseRefetchRef = useRef(false);

  // Track if we've detected network issues (immediate stop on first network error)
  const networkErrorDetectedRef = useRef(false);
  const rateLimitResumeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const pauseResumeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const visibilityResumeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  // Track last error message to avoid duplicate logging
  const lastErrorRef = useRef<string | null>(null);

  // Track last successful update time
  const lastSuccessfulUpdateRef = useRef<number | null>(null);

  // Track previous visibility state to detect visibility changes
  const prevVisibilityRef = useRef<boolean>(isAppVisible);

  // Memoize asset filtering to prevent unnecessary recalculations
  const activeAssets = useMemo(() => assets.filter(a => a.isActive), [assets]);

  // Fetch all asset prices using unified API
  const {
    data: assetPrices,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['assetPrices', activeAssets.map(a => a.id)],
    queryFn: async () => {
      if (activeAssets.length === 0) return [];

      // 先检查网络连通性
      if (!isOnline) {
        // 网络不通，直接抛出错误，不进行任何请求
        throw new Error('Network offline');
      }

      try {
        // Use unified API to get prices for all assets
        const prices = await unifiedApiService.getAssetPrices(
          activeAssets as any,
          {
            timeout: 10000,
            context: 'asset-data-fetch',
          }
        );

        // Reset failure count on success
        consecutiveFailuresRef.current = 0;
        shouldPauseRefetchRef.current = false;
        setShouldPauseRefetch(false);
        networkErrorDetectedRef.current = false;
        lastErrorRef.current = null;

        // Record successful update time
        lastSuccessfulUpdateRef.current = Date.now();

        return prices;
      } catch (error) {
        // 检查是否是所有 provider 都失败的错误
        const isAllProvidersFailed =
          error &&
          typeof error === 'object' &&
          'code' in error &&
          (error as ApiError).code === 'SERVER_ERROR' &&
          ((error as any).message?.includes('All providers failed') ||
            (error as any).message?.includes('All providers failed to fetch'));

        // Check if it's a network-related error
        const isNetworkError =
          error &&
          typeof error === 'object' &&
          'code' in error &&
          ((error as ApiError).code === 'NETWORK_ERROR' ||
            (error as ApiError).code === 'TIMEOUT_ERROR' ||
            (error as any).message?.includes('network') ||
            (error as any).message?.includes('Network') ||
            (error as any).message?.includes('fetch') ||
            (error as any).message?.includes('Failed to fetch') ||
            (error as any).message === 'Network offline');

        // 如果所有 provider 都失败，立即暂停刷新，等待间隔时间后再尝试
        if (isAllProvidersFailed) {
          // 立即设置 ref，阻止后续查询
          shouldPauseRefetchRef.current = true;
          networkErrorDetectedRef.current = true;
          setShouldPauseRefetch(true);
          // 立即取消正在进行的请求
          unifiedApiService.cancelRequests('asset-data-fetch');
          // 只记录一次警告
          const errorMsg = (error as any)?.message || String(error);
          if (lastErrorRef.current !== errorMsg) {
            console.warn('All providers failed. Will retry after interval.');
            lastErrorRef.current = errorMsg;
          }
          throw error;
        }

        // If it's a network error, pause refresh until next interval
        if (isNetworkError) {
          // 立即设置 ref，阻止后续查询
          shouldPauseRefetchRef.current = true;
          networkErrorDetectedRef.current = true;
          setShouldPauseRefetch(true);
          // 立即取消正在进行的请求
          unifiedApiService.cancelRequests('asset-data-fetch');
          // 只记录一次警告
          const errorMsg = (error as any)?.message || String(error);
          if (lastErrorRef.current !== errorMsg) {
            console.warn('Network error detected. Will retry after interval.');
            lastErrorRef.current = errorMsg;
          }
          throw error;
        }

        // Rate limit: pause refresh temporarily (do not count as "consecutive failure" freeze)
        const isRateLimitError =
          error &&
          typeof error === 'object' &&
          'code' in error &&
          (error as ApiError).code === 'RATE_LIMIT_ERROR';
        if (isRateLimitError) {
          const retryAfter = (error as ApiError).retryAfter ?? 60000;
          shouldPauseRefetchRef.current = true;
          setShouldPauseRefetch(true);
          // 立即取消正在进行的请求
          unifiedApiService.cancelRequests('asset-data-fetch');
          if (rateLimitResumeTimerRef.current) {
            clearTimeout(rateLimitResumeTimerRef.current);
          }
          rateLimitResumeTimerRef.current = setTimeout(() => {
            consecutiveFailuresRef.current = 0;
            shouldPauseRefetchRef.current = false;
            setShouldPauseRefetch(false);
            networkErrorDetectedRef.current = false;
            lastErrorRef.current = null;
          }, retryAfter);
          throw error;
        }

        // 其他错误：失败就失败了，立即暂停刷新，等待下次间隔时间再尝试
        shouldPauseRefetchRef.current = true;
        setShouldPauseRefetch(true);
        // 立即取消正在进行的请求
        unifiedApiService.cancelRequests('asset-data-fetch');
        throw error;
      }
    },
    enabled:
      activeAssets.length > 0 &&
      isAppVisible &&
      !shouldPauseRefetch &&
      !shouldPauseRefetchRef.current,
    refetchInterval: () => {
      // 只在应用可见时进行自动刷新
      if (!isAppVisible) {
        return false;
      }

      // 如果暂停刷新（包括网络错误、所有 provider 失败等情况），不进行自动刷新
      // 等待 useEffect 中的定时器重置状态后再尝试
      if (
        shouldPauseRefetch ||
        shouldPauseRefetchRef.current ||
        networkErrorDetectedRef.current
      ) {
        return false;
      }

      // 如果网络离线，等待网络恢复
      if (!isOnline) {
        return false;
      }

      // 正常情况：按配置的间隔时间刷新
      return updateInterval * 1000;
    },
    staleTime: (updateInterval * 1000) / 2,
    retry: (failureCount, error) => {
      // 完全禁用重试，失败就失败了，等待下次间隔时间再尝试
      // 这样可以避免快速循环报错导致页面卡死
      return false;
    },
    retryDelay: 2000,
  });

  // Update asset prices when data changes
  useEffect(() => {
    if (assetPrices && assetPrices.length > 0) {
      const updatedIds = new Set<string>();
      assetPrices.forEach(price => {
        updateAssetPrice(price.id, price);
        updatedIds.add(price.id);
      });

      // 对于没有成功获取价格的资产，如果它们当前不是 error 状态，设置为 loading
      // 这样可以避免错误状态覆盖成功的数据
      activeAssets.forEach(asset => {
        if (!updatedIds.has(asset.id) && asset.priceStatus !== 'error') {
          // 如果资产已经有价格数据，保持 success 状态
          // 如果没有价格数据，设置为 loading（等待下次更新）
          if (asset.price === undefined || asset.price === null) {
            updateAssetPriceStatus(asset.id, 'loading');
          }
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetPrices]); // updateAssetPrice is stable from zustand store

  // Handle errors - update asset status (不记录错误到errorStore，避免显示错误信息)
  // 只对没有成功获取价格的资产设置错误状态
  useEffect(() => {
    if (error) {
      // 避免重复打印相同的错误
      const errorMsg = (error as any)?.message || String(error);
      if (lastErrorRef.current !== errorMsg) {
        // 只在错误消息变化时打印一次
        console.error('Asset price fetch error:', error);
        lastErrorRef.current = errorMsg;
      }

      // 只对没有成功获取价格的资产设置错误状态
      // 使用 setTimeout 确保不会阻塞 UI 线程，并且让价格更新先完成
      setTimeout(() => {
        const updatedIds =
          assetPrices && assetPrices.length > 0
            ? new Set(assetPrices.map(p => p.id))
            : new Set<string>();

        const failedAssets = activeAssets.filter(
          asset =>
            !updatedIds.has(asset.id) &&
            (asset.price === undefined || asset.price === null)
        );

        const staleAssets = activeAssets.filter(
          asset =>
            !updatedIds.has(asset.id) &&
            asset.price !== undefined &&
            asset.price !== null
        );

        const isRateLimitError =
          error &&
          typeof error === 'object' &&
          'code' in error &&
          (error as ApiError).code === 'RATE_LIMIT_ERROR';

        if (failedAssets.length > 0) {
          if (!isRateLimitError) {
            // 不记录错误到 errorStore，避免在价格页面显示错误信息
            // 只设置错误状态，让UI显示"加载失败"红字

            // 只对真正失败的资产设置错误状态
            failedAssets.forEach(asset => {
              updateAssetPriceStatus(asset.id, 'error');
            });
          } else {
            // Rate limit is temporary: keep assets in loading rather than hard error
            failedAssets.forEach(asset => {
              updateAssetPriceStatus(asset.id, 'loading');
            });
          }
        }

        // 已有价格但本轮未更新的资产标记为 stale
        staleAssets.forEach(asset => {
          if (asset.priceStatus !== 'stale') {
            updateAssetPriceStatus(asset.id, 'stale');
          }
        });
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, activeAssets, assetPrices]); // updateAssetPriceStatus and handleError are stable

  // Reset pause state when network comes back online or after interval
  useEffect(() => {
    if (!isOnline) {
      // 网络离线时，立即暂停刷新
      shouldPauseRefetchRef.current = true;
      setShouldPauseRefetch(true);
      networkErrorDetectedRef.current = true;
      unifiedApiService.cancelRequests('asset-data-fetch');
      if (pauseResumeTimerRef.current) {
        clearTimeout(pauseResumeTimerRef.current);
        pauseResumeTimerRef.current = null;
      }
      return undefined;
    }

    // 应用隐藏时，不启动恢复计时器
    if (!isAppVisible) {
      if (pauseResumeTimerRef.current) {
        clearTimeout(pauseResumeTimerRef.current);
        pauseResumeTimerRef.current = null;
      }
      return undefined;
    }

    // 如果暂停刷新（包括所有 provider 失败等情况），等待间隔时间后再重置状态
    if (shouldPauseRefetch || shouldPauseRefetchRef.current) {
      if (pauseResumeTimerRef.current) {
        clearTimeout(pauseResumeTimerRef.current);
      }
      pauseResumeTimerRef.current = setTimeout(() => {
        consecutiveFailuresRef.current = 0;
        shouldPauseRefetchRef.current = false;
        setShouldPauseRefetch(false);
        networkErrorDetectedRef.current = false;
        lastErrorRef.current = null;
        console.log('Resuming auto-refresh after interval.');
      }, updateInterval * 1000);

      return () => {
        if (pauseResumeTimerRef.current) {
          clearTimeout(pauseResumeTimerRef.current);
          pauseResumeTimerRef.current = null;
        }
      };
    }

    return undefined;
  }, [isOnline, isAppVisible, shouldPauseRefetch, updateInterval]);

  // Handle visibility changes: stop requests when hidden, check update time when shown
  useEffect(() => {
    const wasVisible = prevVisibilityRef.current;
    const isNowVisible = isAppVisible;

    if (!isNowVisible) {
      // 窗口隐藏：停止所有网络请求
      unifiedApiService.cancelRequests('asset-data-fetch');
      // 暂停自动刷新并清理计时器
      shouldPauseRefetchRef.current = true;
      setShouldPauseRefetch(true);
      if (pauseResumeTimerRef.current) {
        clearTimeout(pauseResumeTimerRef.current);
        pauseResumeTimerRef.current = null;
      }
      if (visibilityResumeTimerRef.current) {
        clearTimeout(visibilityResumeTimerRef.current);
        visibilityResumeTimerRef.current = null;
      }
      // 清除rate limit定时器
      if (rateLimitResumeTimerRef.current) {
        clearTimeout(rateLimitResumeTimerRef.current);
        rateLimitResumeTimerRef.current = null;
      }
      // 更新可见性状态
      prevVisibilityRef.current = isNowVisible;
      return undefined;
    } else if (!wasVisible && isNowVisible) {
      // 窗口从隐藏变为显示：恢复自动刷新
      shouldPauseRefetchRef.current = false;
      setShouldPauseRefetch(false);
      networkErrorDetectedRef.current = false;
      lastErrorRef.current = null;

      // 窗口从隐藏变为显示：检查上次成功更新的时间
      const now = Date.now();
      const lastUpdate = lastSuccessfulUpdateRef.current;
      const intervalMs = updateInterval * 1000;

      if (lastUpdate === null) {
        // 从未更新过，立即更新
        refetch();
        prevVisibilityRef.current = isNowVisible;
        return undefined;
      } else {
        const timeSinceLastUpdate = now - lastUpdate;
        if (timeSinceLastUpdate >= intervalMs) {
          // 时间间隔已到，立即更新
          refetch();
          prevVisibilityRef.current = isNowVisible;
          return undefined;
        } else {
          // 时间间隔未到，等待剩余时间后再更新
          const remainingTime = intervalMs - timeSinceLastUpdate;
          if (visibilityResumeTimerRef.current) {
            clearTimeout(visibilityResumeTimerRef.current);
          }
          visibilityResumeTimerRef.current = setTimeout(() => {
            refetch();
          }, remainingTime);

          prevVisibilityRef.current = isNowVisible;
          return () => {
            if (visibilityResumeTimerRef.current) {
              clearTimeout(visibilityResumeTimerRef.current);
              visibilityResumeTimerRef.current = null;
            }
          };
        }
      }
    }

    // 更新可见性状态
    prevVisibilityRef.current = isNowVisible;
    return undefined;
  }, [isAppVisible, updateInterval, refetch]);

  // Cleanup effect - ensure all queries are properly cancelled on unmount
  useEffect(() => {
    return () => {
      // Cancel any ongoing requests
      unifiedApiService.cancelRequests('asset-data-fetch');

      if (rateLimitResumeTimerRef.current) {
        clearTimeout(rateLimitResumeTimerRef.current);
        rateLimitResumeTimerRef.current = null;
      }
      if (pauseResumeTimerRef.current) {
        clearTimeout(pauseResumeTimerRef.current);
        pauseResumeTimerRef.current = null;
      }
      if (visibilityResumeTimerRef.current) {
        clearTimeout(visibilityResumeTimerRef.current);
        visibilityResumeTimerRef.current = null;
      }

      // React Query handles cleanup automatically, but we track it for memory leak detection
      lifecycleTracker.registerCleanup(() => {
        // Any additional cleanup if needed
      });
    };
  }, [lifecycleTracker]);

  // Manual refresh function that resets pause state
  const manualRefetch = useCallback(async () => {
    consecutiveFailuresRef.current = 0;
    shouldPauseRefetchRef.current = false;
    setShouldPauseRefetch(false);
    networkErrorDetectedRef.current = false;
    lastErrorRef.current = null;
    return refetch();
  }, [refetch]);

  return {
    assets,
    isLoading,
    error,
    refetch: manualRefetch,
    isPaused: shouldPauseRefetch,
  };
};
